import React, { useState, useEffect, useCallback } from 'react';
import { listGeminiModels, fetchGeminiStatus, GeminiModelStatus } from '../utils/geminiApi';
import './LandingPage.css';

interface LandingPageProps {
  onSelectIELTS: () => void;
  onSelectTEF: () => void;
  onSelectRandomQuestion: () => void;
  onSelectRandomIeltsSpeaking: () => void;
  onSelectVideoInterview: () => void;
}

interface ModelInfo {
  name: string;
  displayName: string;
  supportedMethods: string[];
  supportsGenerateContent: boolean;
}

interface ModelsData {
  v1?: ModelInfo[];
  v1beta?: ModelInfo[];
  [key: string]: ModelInfo[] | undefined;
}

const LandingPage: React.FC<LandingPageProps> = ({
  onSelectIELTS,
  onSelectTEF,
  onSelectRandomQuestion,
  onSelectRandomIeltsSpeaking,
  onSelectVideoInterview
}) => {
  const [models, setModels] = useState<ModelsData>({});
  const [status, setStatus] = useState<GeminiModelStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const currentModel = status?.configuredModel || 'gemini-2.5-flash';
  const currentApiVersion = status?.apiVersion || 'v1';

  const refreshStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    setStatusError(null);

    try {
      const [statusData, modelsData] = await Promise.all([
        fetchGeminiStatus(),
        listGeminiModels().catch((err: any) => {
          setError(err.message || '모델 목록을 가져오는 중 오류가 발생했습니다.');
          return {} as ModelsData;
        })
      ]);
      setStatus(statusData);
      setModels(modelsData as ModelsData);
    } catch (err: any) {
      console.error('Error fetching Gemini status:', err);
      setStatusError(err.message || 'AI 상태를 확인하지 못했습니다.');
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshStatus();
  }, [refreshStatus]);

  const renderModelList = (version: 'v1' | 'v1beta', modelsList: ModelInfo[] | undefined) => {
    if (!modelsList || modelsList.length === 0) {
      return <li className="model-item">모델을 불러올 수 없습니다.</li>;
    }

    return modelsList.map((model) => {
      const isCurrent = version === currentApiVersion && model.name === currentModel;
      return (
        <li
          key={model.name}
          className={`model-item ${model.supportsGenerateContent ? 'supported' : 'unsupported'}`}
        >
          {model.supportsGenerateContent ? '✅' : '❌'} {model.name}
          {isCurrent && <span className="model-badge">현재 사용</span>}
        </li>
      );
    });
  };

  const statusClass = status
    ? status.usable
      ? 'ok'
      : 'bad'
    : statusError
      ? 'bad'
      : 'pending';

  const statusLabel = loading
    ? '확인 중…'
    : status?.usable
      ? '사용 가능'
      : status || statusError
        ? '사용 불가'
        : '상태 미확인';

  return (
    <div className="landing-page">
      <div className="landing-container">
        <h1>earth</h1>
        <p className="landing-subtitle">원하는 시험을 선택하세요</p>

        <div className="exam-buttons">
          <button
            onClick={onSelectIELTS}
            className="exam-button ielts-button"
          >
            <div className="exam-icon">🇬🇧</div>
            <div className="exam-title">IELTS</div>
            <div className="exam-description">IELTS Speaking Practice</div>
          </button>

          <button
            onClick={onSelectTEF}
            className="exam-button tef-button"
          >
            <div className="exam-icon">🇫🇷</div>
            <div className="exam-title">TEF Canada</div>
            <div className="exam-description">Test d'évaluation de français</div>
          </button>
        </div>

        <div className="exam-buttons random-speaking-row">
          <button
            onClick={onSelectRandomIeltsSpeaking}
            className="exam-button random-ielts-speaking-button"
          >
            <div className="exam-icon">🎧</div>
            <div className="exam-title">랜덤 영어 스피킹</div>
            <div className="exam-description">IELTS Speaking 랜덤 + 자동 음성</div>
          </button>
          <button
            onClick={onSelectRandomQuestion}
            className="exam-button random-question-button"
          >
            <div className="exam-icon">🎲</div>
            <div className="exam-title">랜덤 문제</div>
            <div className="exam-description">영/불어 스피킹·라이팅 랜덤</div>
          </button>
          <button
            onClick={onSelectVideoInterview}
            className="exam-button video-interview-button"
          >
            <div className="exam-icon">🎥</div>
            <div className="exam-title">영상 면접</div>
            <div className="exam-description">브라우저가 질문을 읽고 3분 답변</div>
          </button>
        </div>

        <div className="model-info-section">
          <div className="model-info-header">
            <h3 className="model-info-title">🤖 AI 모델 정보</h3>
            <button
              type="button"
              className="model-refresh-button"
              onClick={refreshStatus}
              disabled={loading}
            >
              {loading ? '확인 중…' : '다시 확인'}
            </button>
          </div>

          <div className={`model-status-card ${statusClass}`}>
            <div className="model-status-row">
              <span className="model-status-label">상태</span>
              <span className={`model-status-badge ${statusClass}`}>{statusLabel}</span>
            </div>

            <div className="model-status-row">
              <span className="model-status-label">모델</span>
              <span className="model-status-value">
                <strong>{currentModel}</strong> ({currentApiVersion})
              </span>
            </div>

            <div className="model-status-row">
              <span className="model-status-label">계정</span>
              <span className="model-status-value">
                {status?.account || (loading ? '확인 중…' : '설정 없음')}
              </span>
            </div>

            <div className="model-status-row">
              <span className="model-status-label">프로젝트</span>
              <span className="model-status-value">
                {status?.projectId
                  ? `${status.projectId}${status.projectNumber ? ` (${status.projectNumber})` : ''}`
                  : status?.projectNumber || (loading ? '확인 중…' : '설정 없음')}
              </span>
            </div>

            <div className="model-status-row">
              <span className="model-status-label">결제</span>
              <span className="model-status-value">
                {status
                  ? status.billingEnabled
                    ? '연결됨 (유료 가능)'
                    : '미연결 (무료 티어)'
                  : loading
                    ? '확인 중…'
                    : '—'}
              </span>
            </div>

            {status?.keyHint && (
              <div className="model-status-row">
                <span className="model-status-label">키</span>
                <span className="model-status-value mono">{status.keyHint}</span>
              </div>
            )}

            {(status?.message || statusError) && (
              <div className="model-status-message">
                {status?.message || statusError}
                {status?.error && (
                  <div className="model-status-error-detail">{status.error}</div>
                )}
              </div>
            )}

            {status && (
              <div className="model-status-checks">
                <span className={status.listed ? 'check-ok' : 'check-bad'}>
                  {status.listed ? '✓' : '✗'} 목록에 존재
                </span>
                <span className={status.probeOk ? 'check-ok' : 'check-bad'}>
                  {status.probeOk ? '✓' : '✗'} 실제 호출
                </span>
                {status.checkedAt && (
                  <span className="check-time">
                    확인: {new Date(status.checkedAt).toLocaleString()}
                  </span>
                )}
              </div>
            )}
          </div>

          <details className="available-models">
            <summary className="models-summary">
              사용 가능한 모델 목록 보기
              {loading && <span className="loading-indicator"> (로딩 중...)</span>}
            </summary>

            {error && (
              <div className="model-error">
                ⚠️ {error}
              </div>
            )}

            {!loading && !error && (
              <div className="models-list">
                {models.v1 && models.v1.length > 0 && (
                  <div className="api-version">
                    <h4>v1 API ({models.v1.length}개 모델)</h4>
                    <ul className="model-list">
                      {renderModelList('v1', models.v1)}
                    </ul>
                  </div>
                )}

                {models.v1beta && models.v1beta.length > 0 && (
                  <div className="api-version">
                    <h4>v1beta API ({models.v1beta.length}개 모델)</h4>
                    <ul className="model-list">
                      {renderModelList('v1beta', models.v1beta)}
                    </ul>
                  </div>
                )}

                {(!models.v1 || models.v1.length === 0) && (!models.v1beta || models.v1beta.length === 0) && (
                  <div className="no-models">모델 목록을 불러올 수 없습니다.</div>
                )}
              </div>
            )}
          </details>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
